import React, { useMemo, useState, useEffect } from 'react';
import {
  ResponsiveContainer, BarChart, PieChart, CartesianGrid, XAxis, YAxis, Tooltip, Legend, Bar, Cell, Pie // Ensure 'Pie' is correctly imported here
} from 'recharts';
import { cn } from '../../lib/utils';

// Define month order for sorting
const monthOrder = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const RevenueGraph = ({ data, title, chartMode = 'monthlyRevenue', theme }) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log("RevenueGraph received data:", data); // Log received data
    console.log("RevenueGraph chartMode:", chartMode); // Log chart mode
  }

  // Define theme-based colors
  const tickColor = theme === 'light' ? '#6b7280' : '#a1a1aa'; // Gray-500 / Zinc-400
  const tooltipBg = theme === 'light' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(31, 31, 46, 0.9)'; // white / #1F1F2E
  const tooltipText = theme === 'light' ? '#1f2937' : '#e5e7eb'; // Gray-800 / Gray-200
  const gridColor = theme === 'light' ? '#e5e7eb' : '#3f3f46'; // Gray-200 / Zinc-700

  // Process data for Bar/Line chart
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return [];
    try {
      const processed = data
        .map(item => {
          const monthParts = item.month?.split(' ') || [];
          const monthName = monthParts[0];
          const year = parseInt(monthParts[1], 10);
          const monthIndex = monthOrder.indexOf(monthName);
          return {
            ...item,
            monthIndex: monthIndex,
            year: year,
            // Format month for display (e.g., "Jan 25")
            displayMonth: monthIndex !== -1 && year ? `${monthName.substring(0, 3)} ${String(year).substring(2)}` : item.month,
            // Ensure amount is a number
            amount: parseFloat(item.amount) || 0
          };
        })
        // Sort chronologically (oldest first for the chart)
        .sort((a, b) => {
          if (a.year !== b.year) return a.year - b.year;
          return a.monthIndex - b.monthIndex;
        });
      if (process.env.NODE_ENV !== 'production') {
        console.log("Processed chartData (for Bar/Line):", processed); // Log processed bar chart data
      }
      return processed;
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error("Error processing chartData:", error);
      }
      return [];
    }
  }, [data]);

  // Process data for Pie chart (Practice Breakdown)
  const practiceData = useMemo(() => {
    if (!data || data.length === 0 || chartMode !== 'practiceBreakdown') return [];
    try {
      const aggregation = data.reduce((acc, monthData) => {
        monthData.invoiceDetails?.forEach(invDetail => {
          const practiceName = invDetail.practiceName || 'Unknown';
          // Ensure amount is treated as a number, default to 0 if invalid
          const amount = parseFloat(invDetail.amount) || 0;
          if (!acc[practiceName]) {
            acc[practiceName] = 0;
          }
          acc[practiceName] += amount;
        });
        return acc;
      }, {});

      const processed = Object.entries(aggregation)
        .map(([name, value]) => ({ name, value }))
        .filter(item => item.value > 0) // Exclude practices with zero revenue
        .sort((a, b) => b.value - a.value); // Sort descending by value

      if (process.env.NODE_ENV !== 'production') {
        console.log("Processed practiceData (for Pie):", processed); // Log processed pie chart data
      }
      return processed;
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error("Error processing practiceData:", error);
      }
      return [];
    }
  }, [data, chartMode]); // Depend on data and chartMode

  // Colors for Pie Chart
  const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  // Custom Tooltip for Bar/Line Chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="p-2 rounded shadow-lg" style={{ backgroundColor: tooltipBg, border: `1px solid ${gridColor}` }}>
          <p className="label font-semibold" style={{ color: tooltipText }}>{`${label}`}</p>
          <p className="intro" style={{ color: payload[0].color || tooltipText }}>
            {`Revenue: £${payload[0].value.toFixed(2)}`}
          </p>
          {/* Add more details if needed, e.g., invoice count */}
        </div>
      );
    }
    return null;
  };

  // Custom Tooltip for Pie Chart
  const CustomPieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload; // Access the payload object
      return (
        <div className="p-2 rounded shadow-lg" style={{ backgroundColor: tooltipBg, border: `1px solid ${gridColor}` }}>
          <p className="label font-semibold" style={{ color: tooltipText }}>{`${data.name}`}</p>
          <p className="intro" style={{ color: payload[0].fill || tooltipText }}>
            {`Revenue: £${data.value.toFixed(2)}`}
          </p>
          {/* Optionally calculate and display percentage */}
          {/* <p style={{ color: tooltipText }}>{`(${(payload[0].percent * 100).toFixed(0)}%)`}</p> */}
        </div>
      );
    }
    return null;
  };

  // Custom label for Pie Chart
  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name, value }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const percentage = (percent * 100).toFixed(0);

    // Only show label if percentage is significant enough (e.g., > 3%)
    if (percentage < 3) return null;

    return (
      <text x={x} y={y} fill={theme === 'light' ? "#333" : "white"} textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" fontSize={10}>
        {`${name} (${percentage}%)`}
      </text>
    );
  };

  // Add state for screen size detection
  const [isMobile, setIsMobile] = useState(false);

  // Add useEffect to detect screen size
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 640); // sm breakpoint
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  return (
    <div className="h-72 sm:h-96 w-full"> {/* Container height */}
      <h3 className={cn("text-center text-sm sm:text-base font-semibold mb-1 sm:mb-4", theme === 'light' ? 'text-gray-800' : 'text-white')}>
        {title}
      </h3>
      <ResponsiveContainer width="100%" height="100%">
        {chartMode === 'monthlyRevenue' ? (
          chartData.length > 0 ? (
            <BarChart 
              data={chartData} 
              margin={{ top: 5, right: 15, left: 5, bottom: 5 }} // Slightly increased right margin for Y-axis labels
            >
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis
                dataKey="displayMonth"
                tick={{ fill: tickColor, fontSize: 10 }}
                angle={-30}
                textAnchor="end"
                height={40}
                interval={0}
              />
              <YAxis
                tick={{ fill: tickColor, fontSize: 10 }}
                tickFormatter={(value) => `£${value >= 1000 ? `${value / 1000}k` : value}`} // Format as thousands or regular
                width={45}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: theme === 'light' ? 'rgba(200, 200, 200, 0.3)' : 'rgba(100, 100, 100, 0.3)' }} />
              <Bar 
                dataKey="amount" 
                fill="#8884d8" 
                name="Revenue" 
                // barSize={20} // REMOVE fixed barSize to allow responsiveness
              />
            </BarChart>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className={cn(theme === 'light' ? 'text-gray-500' : 'text-gray-400')}>No monthly revenue data available.</p>
            </div>
          )
        ) : chartMode === 'practiceBreakdown' ? (
          practiceData.length > 0 ? (
            <PieChart>
              <Pie
                data={practiceData}
                cx="50%"
                cy={isMobile ? "40%" : "50%"} // Keep center positioning
                labelLine={false}
                // label={renderCustomizedLabel} // Optional: Add back if needed
                outerRadius={isMobile ? 70 : 100} // Increase mobile radius from 50 to 70
                fill="#8884d8"
                dataKey="value"
                nameKey="name"
              >
                {practiceData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomPieTooltip />} />
              <Legend
                 layout={isMobile ? "horizontal" : "vertical"}
                 align={isMobile ? "center" : "right"}
                 verticalAlign={isMobile ? "bottom" : "middle"}
                 iconSize={isMobile ? 8 : 10}
                 wrapperStyle={{ 
                   fontSize: isMobile ? '10px' : '12px', 
                   color: tickColor, 
                   paddingLeft: isMobile ? '0px' : '10px',
                   paddingTop: isMobile ? '0px' : '0px',
                   paddingBottom: isMobile ? '5px' : '0px',
                   maxWidth: isMobile ? '100%' : 'auto',
                   marginTop: isMobile ? '-15px' : '0px' // Pull legend even closer to chart
                 }}
                 formatter={(value, entry) => (
                   <span style={{ color: tickColor }}>
                     {isMobile ? 
                       `${value.length > 12 ? value.substring(0, 12) + '...' : value}` : 
                       `${value} (£${entry.payload.value.toFixed(2)})`
                     }
                   </span>
                 )}
               />
            </PieChart>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className={cn(theme === 'light' ? 'text-gray-500' : 'text-gray-400')}>No practice breakdown data available.</p>
            </div>
          )
        ) : (
           <div className="flex items-center justify-center h-full">
             <p className={cn(theme === 'light' ? 'text-gray-500' : 'text-gray-400')}>Invalid chart mode selected.</p>
           </div>
        )}
      </ResponsiveContainer>
    </div>
  );
};

export default RevenueGraph;
